// The chart's seat dimensions (CHART-DIMENSIONS; docs/design/2026-10-21-chart-dimensions.md),
// the pure part: which dimensions exist and what they read, the presets, what a viewer has
// chosen (remembered per company in this browser) over the company's default, and the "my
// line" focus. The server (`GET org-chart/dimensions`) decides what a viewer may see; this
// file only decides what the viewer wants drawn. Vocabulary is the spec's (01): seat, holder,
// supervisor, team, responsibility, scorecard band, task, budget vs actual.
import type { OrgChartDimensions } from "../../api/types";
import type { ChartSeat, OrgChart } from "./chart-model";

export type Dimension = OrgChartDimensions["dimensions"][number];
export type GatedDimension = keyof OrgChartDimensions["visibility"];
export type Level = OrgChartDimensions["visibility"][GatedDimension];
export type SeatDimensions = OrgChartDimensions["seats"][number];

/** Every dimension a card can carry, in the order the card draws them. */
export const DIMENSIONS: readonly Dimension[] = ["team", "responsibilities", "kpi", "load", "cost"];
/**
 * The dimensions an admin chooses an audience for (the floor has none). `overall` is the
 * Overall lens (CHART-LENSES): a summary of the others with an audience of its own.
 */
export const GATED: readonly GatedDimension[] = ["kpi", "load", "cost", "overall", "keystones"];
export const LEVELS: readonly Level[] = ["everyone", "reporting_line", "holder", "managers"];

export const DIMENSION_LABELS: Record<Dimension, string> = {
  team: "Teams",
  responsibilities: "Responsibilities",
  kpi: "KPI status",
  load: "Load",
  cost: "Cost",
};

/** What each dimension reads, in one line (the Dimensions menu and Setup › Chart say it). */
export const DIMENSION_HINTS: Record<Dimension, string> = {
  team: "The teams the seat is a member of",
  responsibilities: "The seat's accountabilities, heaviest first",
  kpi: "The seat's scorecard band for the focused week",
  load: "Open tasks assigned to the seat, overdue and estimated hours",
  cost: "The seat's month against its budget (pay where you may read it)",
};

export const LEVEL_LABELS: Record<Level, string> = {
  everyone: "Everyone",
  reporting_line: "Reporting line",
  holder: "Holder and managers",
  managers: "Admins only",
};

export const LEVEL_HINTS: Record<Level, string> = {
  everyone: "Every member who can open the chart",
  reporting_line: "The seat's holders, the holders of the seats above it, and admins",
  holder: "The seat's holders and admins",
  managers: "Owners, admins and the agency operator",
};

/** The company's audiences before an admin changes them (the API's `DEFAULT_VISIBILITY`). */
export const DEFAULT_VISIBILITY: Record<GatedDimension, Level> = {
  kpi: "reporting_line",
  load: "reporting_line",
  cost: "managers",
  overall: "reporting_line",
  // CHART-KB: a seat's quarterly priorities are read by every member (05 `goal` viewer).
  keystones: "everyone",
};

/** What the audience rows call each gated key (the lens name where there is one). */
export const GATED_LABELS: Record<GatedDimension, string> = {
  kpi: "Performance",
  load: "Tasks",
  cost: "Finance",
  overall: "Overall",
  keystones: "Priorities",
};

export const PRESETS = ["structure", "accountability", "performance", "capacity", "full"] as const;
export type Preset = (typeof PRESETS)[number];

export const PRESET_LABELS: Record<Preset, string> = {
  structure: "Structure",
  accountability: "Accountability",
  performance: "Performance",
  capacity: "Capacity",
  full: "Full",
};

/** What each preset turns on; the holder, title and reporting line are always drawn. */
export const PRESET_DIMENSIONS: Record<Preset, readonly Dimension[]> = {
  structure: ["team"],
  accountability: ["team", "responsibilities"],
  performance: ["responsibilities", "kpi"],
  capacity: ["team", "load", "cost"],
  full: DIMENSIONS,
};

/** The company default when the admin has not chosen one. */
export const DEFAULT_DIMENSIONS: readonly Dimension[] = PRESET_DIMENSIONS.accountability;

const same = (a: ReadonlySet<Dimension>, b: readonly Dimension[]) =>
  a.size === b.length && b.every((d) => a.has(d));

/** The preset a choice matches exactly, or null (a custom mix). */
export function presetOf(dims: ReadonlySet<Dimension>): Preset | null {
  return PRESETS.find((p) => same(dims, PRESET_DIMENSIONS[p])) ?? null;
}

// The viewer's choice, per company, in this browser ----------------------------------------------

const PREFS_KEY = (companyId: string) => `companyos.chart.dimensions.${companyId}`;

const isDimension = (v: unknown): v is Dimension =>
  typeof v === "string" && (DIMENSIONS as readonly string[]).includes(v);

/** The dimensions this browser's viewer chose for the company, or null for "the default". */
export function readDimensionPrefs(companyId: string): Set<Dimension> | null {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY(companyId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter(isDimension)) : null;
  } catch {
    return null;
  }
}

export function writeDimensionPrefs(companyId: string, dims: ReadonlySet<Dimension> | null): void {
  try {
    if (dims === null) window.localStorage.removeItem(PREFS_KEY(companyId));
    else window.localStorage.setItem(PREFS_KEY(companyId), JSON.stringify([...dims]));
  } catch {}
}

/**
 * What to draw: the viewer's choice when there is one, else the company default; either way
 * only what the company serves (`served`: no `kpi` without the add-on). `overlay` is the old
 * `?overlay=kpi|money` link: it adds its dimension so a shared link still shows what it meant.
 */
export function effectiveDimensions(
  prefs: ReadonlySet<Dimension> | null,
  companyDefault: readonly Dimension[] | undefined,
  served: readonly Dimension[] | undefined,
  overlay?: "kpi" | "money",
): Set<Dimension> {
  const base = new Set(prefs ?? companyDefault ?? DEFAULT_DIMENSIONS);
  if (overlay === "kpi") base.add("kpi");
  if (overlay === "money") base.add("cost");
  if (served) for (const d of base) if (!served.includes(d)) base.delete(d);
  return base;
}

/** Whether the dimensions query is needed at all (teams come with the chart itself). */
export function needsDimensions(dims: ReadonlySet<Dimension>): boolean {
  return [...dims].some((d) => d !== "team");
}

// Focus: "my line" ------------------------------------------------------------------------------

/**
 * The seats of the viewer's line: the seats it holds, everything above them (its supervisors,
 * their supervisors) and everything below (its reports, their reports). Empty when the viewer
 * holds no seat in the chart.
 */
export function myLine(
  chart: Pick<OrgChart, "seats">,
  sup: ReadonlyMap<string, string | null>,
  membershipIds: readonly string[],
): Set<string> {
  const mine = chart.seats
    .filter((s) => s.holders.some((h) => membershipIds.includes(h.membership_id)))
    .map((s) => s.id);
  const out = new Set<string>();
  for (const id of mine) {
    out.add(id);
    let cur = sup.get(id) ?? null;
    while (cur && !out.has(cur)) {
      out.add(cur);
      cur = sup.get(cur) ?? null;
    }
  }
  const kids = new Map<string, string[]>();
  for (const [s, p] of sup) if (p) kids.set(p, [...(kids.get(p) ?? []), s]);
  const pending = [...mine];
  while (pending.length > 0) {
    const id = pending.pop() as string;
    for (const k of kids.get(id) ?? [])
      if (!out.has(k)) {
        out.add(k);
        pending.push(k);
      }
  }
  return out;
}

// Rendering helpers --------------------------------------------------------------------------------

/** "3 open · 1 overdue · 4.5 h" (the hours only when there is an estimate). */
export function loadText(load: NonNullable<SeatDimensions["load"]>): string {
  const parts = [`${load.open} open`];
  if (load.overdue > 0) parts.push(`${load.overdue} overdue`);
  if (load.estimate_minutes > 0) parts.push(`${hoursText(load.estimate_minutes)} h`);
  return parts.join(" · ");
}

/** Minutes as hours with one decimal when needed: 90 → "1.5", 480 → "8". */
export function hoursText(minutes: number): string {
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1).replace(/\.0$/, "");
}

/** The tone of a load row: overdue work is a warning, more than half overdue a danger. */
export function loadTone(
  load: NonNullable<SeatDimensions["load"]>,
): "neutral" | "warning" | "danger" {
  if (load.overdue === 0) return "neutral";
  return load.overdue * 2 >= load.open ? "danger" : "warning";
}

/** The seats a search, a scope and the focus leave: the filter the canvas draws. */
export function keepLine(seats: readonly ChartSeat[], line: ReadonlySet<string> | null) {
  return line ? seats.filter((s) => line.has(s.id)) : seats;
}
